import {
  googleMapsOpenInMapsLink,
  googleMapsLocationLink,
} from '@user/components/HereMap/utils/googleMapsLinks'
import {
  ErrorModalState,
  UseDataReturn,
} from '../DrawerContentsSingle/hooks/useData'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { useCallback, useMemo, useState } from 'react'
import { EntityDetail } from '@user/store/map/typings'
import { useCheckPermissions } from '@user/store/authorization'
import { LEAD_STATUSES } from '@user/components/Leads/LeadCard/typings'
import { logger } from '@user/utils/logger'
import { getAgreementConfigs, getForms } from '@user/store/agreements/api'
import { clearAgreementConfigs } from '@user/store/agreements/slice'
import { FM_FORM_STATUSES } from '@user/store/agreements/typings'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'

type Props = {
  addressDisplay: UseDataReturn['addressDisplay']
  serviceLocation: LocationDTO
  entityDetails: EntityDetail
  setErrorModal: (state: ErrorModalState) => void
  setDrawerOpen: (val: boolean) => void
  formPacketEnabled: boolean
  onReviewPendingLead?: (draftId: string) => void
}

export function useHandlers(props: Props) {
  const {
    addressDisplay,
    serviceLocation,
    setErrorModal,
    setDrawerOpen,
    entityDetails,
    formPacketEnabled,
    onReviewPendingLead,
  } = props

  const dispatch = useAppDispatch()
  const { entityType, salesFunnelStage, id } = entityDetails
  const [isAgreementDrawerLoading, setIsAgreementDrawerLoading] =
    useState(false)

  const [canUpdateLeads, canUpdateOpportunities] = useCheckPermissions(
    'leads_update',
    'opportunities_update',
  )
  const isDisqualifiedLead = useMemo(() => {
    return (
      !!salesFunnelStage &&
      salesFunnelStage.toLowerCase() === LEAD_STATUSES.Disqualified
    )
  }, [salesFunnelStage])

  const isClosedOpportunity = useMemo(() => {
    return (
      !!salesFunnelStage &&
      (salesFunnelStage.toLowerCase() === 'closed/won' ||
        salesFunnelStage.toLowerCase() === 'closed/lost')
    )
  }, [salesFunnelStage])

  const canUpdate = useMemo(() => {
    return canUpdateLeads || canUpdateOpportunities
  }, [canUpdateLeads, canUpdateOpportunities])

  const isClosed = useMemo(() => {
    return isDisqualifiedLead || isClosedOpportunity
  }, [isClosedOpportunity, isDisqualifiedLead])

  const readOnly = useMemo(() => {
    return (
      !canUpdateLeads ||
      !canUpdateOpportunities ||
      isDisqualifiedLead ||
      isClosedOpportunity
    )
  }, [
    canUpdateLeads,
    canUpdateOpportunities,
    isClosedOpportunity,
    isDisqualifiedLead,
  ])

  function handleDirectionsClick() {
    if (!addressDisplay) {
      logger.error('No address display available for directions')
      return
    }
    const url = googleMapsOpenInMapsLink(addressDisplay)
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  function handlePhoneClick() {
    if (!serviceLocation.phoneNumber) {
      return
    }
    const phoneNumber = serviceLocation.phoneNumber.replace(/\D/g, '') // Extract digits from phone number
    if (phoneNumber) {
      window.open(`tel:${phoneNumber}`, '_blank', 'noopener,noreferrer')
    } else {
      logger.error('No valid phone number found in service location')
    }
  }

  function handleViewDetailClick(entityId: string, variant: string) {
    if (!entityId) return
    switch (variant) {
      case 'lead':
        window.open(`/#/leads/${entityId}`, '_blank', 'noopener,noreferrer')
        break
      case 'opportunity':
        window.open(
          `/#/opportunities/${entityId}`,
          '_blank',
          'noopener,noreferrer',
        )
        break
      case 'pendinglead':
      case 'draftlead':
        if (onReviewPendingLead) {
          onReviewPendingLead(entityId)
        } else {
          window.open(
            `/#/leads/addLead?draftId=${entityId}`,
            '_blank',
            'noopener,noreferrer',
          )
        }
        break
      default:
        logger.error(`Unsupported variant for view detail: ${variant}`)
        break
    }
  }

  function handleShareClick() {
    if (!addressDisplay) {
      logger.error('No address display available for sharing')
      return
    }
    const url = googleMapsLocationLink(addressDisplay)
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const onOpenAgreementDrawer = useCallback(async () => {
    if (
      (entityType === ENTITY_TYPES.Lead && !canUpdateLeads) ||
      (entityType === ENTITY_TYPES.Opportunity && !canUpdateOpportunities)
    ) {
      setErrorModal({
        open: true,
        title: 'Service / Agreement',
        message: 'You don’t have permission to perform this action.',
      })
      return
    }

    if (
      (entityType === ENTITY_TYPES.Lead && isDisqualifiedLead) ||
      (entityType === ENTITY_TYPES.Opportunity && isClosedOpportunity)
    ) {
      setErrorModal({
        open: true,
        title: 'Service / Agreement',
        message:
          'You cannot add services or create an agreement for a closed record.',
      })
      return
    }

    if (!formPacketEnabled) {
      setDrawerOpen(true)
      return
    }

    setIsAgreementDrawerLoading(true)
    try {
      let openedExistingDraft = false
      const formsResult = await dispatch(
        getForms({
          externalEntityId: id.toString(),
          externalEntityType: entityType.toString(),
        }),
      )

      if (getForms.fulfilled.match(formsResult)) {
        const drafts = formsResult.payload.items.filter(
          (f) => f.status === FM_FORM_STATUSES.Draft,
        )
        if (drafts.length > 0) {
          const mostRecentDraft = [...drafts].sort(
            (a, b) =>
              new Date(b.modifiedDate ?? b.createdDate).getTime() -
              new Date(a.modifiedDate ?? a.createdDate).getTime(),
          )[0]
          const configsResult = await dispatch(
            getAgreementConfigs(mostRecentDraft.id),
          )
          openedExistingDraft =
            getAgreementConfigs.fulfilled.match(configsResult)
        }
      }

      if (!openedExistingDraft) {
        dispatch(clearAgreementConfigs())
      }

      setDrawerOpen(true)
    } finally {
      setIsAgreementDrawerLoading(false)
    }
  }, [
    canUpdateLeads,
    canUpdateOpportunities,
    dispatch,
    entityType,
    formPacketEnabled,
    id,
    isClosedOpportunity,
    isDisqualifiedLead,
    setDrawerOpen,
    setErrorModal,
  ])

  return {
    handleDirectionsClick,
    handlePhoneClick,
    handleViewDetailClick,
    handleShareClick,
    onOpenAgreementDrawer,
    isAgreementDrawerLoading,
    readOnly,
    canUpdate,
    isClosed,
  }
}
